import { useState } from 'react'
import { PageContainer, ProTable, type ProColumns } from '@ant-design/pro-components'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Alert, App, Button, Popconfirm, Space, Tag } from 'antd'
import dayjs from 'dayjs'
import type { SessionAccount } from '@super-mega/server/contracts/auth'
import type { ExpenseRow } from '@super-mega/server/contracts/expense'
import { RANGE_PRESETS } from '@/const/range-presets.ts'
import { useMemberCreatorOptions } from '@/features/access/index.ts'
import { Authorized } from '@/features/authorization/authorized.tsx'
import { FinanceSelect } from '@/features/bill/index.ts'
import { UserAvatarName } from '@/features/profile/index.ts'
import { usePageTableNavigation, type PageTableNavigate } from '@/hooks/use-page-table.ts'
import { useTRPC } from '@/trpc/react.ts'
import { getCreatedAtSearch, getDateRangeFormValue, type DateRangeFormValue } from '@/utils/list-query.ts'
import { formatAmount } from '@/utils/money.ts'
import { ExpenseEditor } from './expense-editor.tsx'
import { ExpenseImport } from './expense-import.tsx'
import { expenseSearchSchema, type ExpenseSearch } from './expense-search.ts'

type Filters = Partial<ExpenseSearch> & { dueRange?: DateRangeFormValue; createdAtRange?: DateRangeFormValue }
export function ExpenseList({
  session,
  search,
  navigate
}: {
  session: SessionAccount
  search: ExpenseSearch
  navigate: PageTableNavigate<ExpenseSearch>
}) {
  const trpc = useTRPC(),
    cache = useQueryClient(),
    { message, modal } = App.useApp()
  const [editing, setEditing] = useState<ExpenseRow | 'new' | null>(null),
    [importing, setImporting] = useState(false),
    [selected, setSelected] = useState<ExpenseRow[]>([])
  const canManage =
    session.isSuperAdmin ||
    session.permissions.some((permission) => permission.startsWith('expense.') && !permission.endsWith('.read'))
  const query = useQuery(trpc.expense.list.queryOptions(search, { placeholderData: keepPreviousData }))
  const remove = useMutation(trpc.expense.remove.mutationOptions())
  const creators = useMemberCreatorOptions('expense.read', search.creatorMemberId)
  const refresh = async () => {
    await Promise.all([
      cache.invalidateQueries(trpc.expense.list.queryFilter()),
      cache.invalidateQueries(trpc.expenseBusiness.list.queryFilter()),
      cache.invalidateQueries(trpc.analytics.summary.queryFilter()),
      cache.invalidateQueries(trpc.analytics.list.queryFilter())
    ])
  }
  const removeRows = async (rows: ExpenseRow[]) => {
    try {
      await remove.mutateAsync({ records: rows.map(({ id, version }) => ({ id, version })) })
      setSelected([])
      await refresh()
      if (rows.length === query.data?.data.length && search.current > 1) {
        void navigate({ search: { ...search, current: search.current - 1 } })
      }
      void message.success('支出记录已删除')
    } catch (error) {
      void message.error(error instanceof Error ? error.message : '删除失败')
    }
  }
  const table = usePageTableNavigation<ExpenseSearch, Filters>({
    search,
    navigate,
    defaultSearch: expenseSearchSchema.parse({}),
    showTotal: (total) => `共 ${total} 条`,
    onQueryChange: () => setSelected([]),
    getFormInitialValues: (value) => ({
      keyword: value.keyword ?? (value.id ? String(value.id) : undefined),
      organizationId: value.organizationId,
      payeeId: value.payeeId,
      expenseBusinessId: value.expenseBusinessId,
      creatorMemberId: value.creatorMemberId,
      dueRange: value.dueFrom && value.dueTo ? [value.dueFrom, value.dueTo] : undefined,
      createdAtRange: getDateRangeFormValue(value.createdFrom, value.createdTo)
    }),
    getSearchFromForm: (value) => ({
      keyword: value.keyword?.trim() || undefined,
      id: undefined,
      organizationId: value.organizationId,
      payeeId: value.payeeId,
      expenseBusinessId: value.expenseBusinessId,
      creatorMemberId: value.creatorMemberId,
      dueFrom: value.dueRange?.[0]
        ? typeof value.dueRange[0] === 'string'
          ? value.dueRange[0]
          : dayjs(value.dueRange[0].toDate()).format('YYYY-MM-DD')
        : undefined,
      dueTo: value.dueRange?.[1]
        ? typeof value.dueRange[1] === 'string'
          ? value.dueRange[1]
          : dayjs(value.dueRange[1].toDate()).format('YYYY-MM-DD')
        : undefined,
      ...getCreatedAtSearch(value.createdAtRange)
    })
  })
  const columns: ProColumns<ExpenseRow>[] = [
    {
      title: '关键词 / ID',
      dataIndex: 'keyword',
      hideInTable: true,
      fieldProps: { placeholder: 'ID、主体、对象、业务或备注' }
    },
    {
      title: 'ID',
      dataIndex: 'id',
      valueType: 'digit',
      search: false,
      copyable: true,
      fieldProps: { min: 1, precision: 0 },
      width: '5rem'
    },
    {
      title: '付款主体',
      dataIndex: 'organizationId',
      valueType: 'select',
      formItemRender: () => <FinanceSelect purpose="expense.read" kind="organization" />,
      render: (_, row) => row.organizationName,
      width: '15rem'
    },
    {
      title: '付款对象',
      dataIndex: 'payeeId',
      valueType: 'select',
      formItemRender: () => <FinanceSelect purpose="expense.read" kind="payee" />,
      render: (_, row) => row.payeeName,
      width: '12rem'
    },
    {
      title: '业务',
      dataIndex: 'expenseBusinessId',
      valueType: 'select',
      formItemRender: () => <FinanceSelect purpose="expense.read" kind="expenseBusiness" />,
      render: (_, row) => <Tag color={row.businessColor}>{row.businessName}</Tag>,
      width: '8rem'
    },
    {
      title: '应付日期',
      dataIndex: 'dueRange',
      fieldProps: { presets: RANGE_PRESETS },
      valueType: 'dateRange',
      hideInTable: true
    },
    { title: '应付日期', dataIndex: 'dueAt', search: false, width: '8rem' },
    {
      title: '金额（元）',
      dataIndex: 'amount',
      renderText: formatAmount,
      search: false,
      align: 'right',
      width: '9rem'
    },
    { title: '备注', dataIndex: 'note', search: false, ellipsis: true, width: '16rem' },
    {
      title: '创建人',
      dataIndex: 'creatorMemberId',
      hideInTable: true,
      valueType: 'select',
      fieldProps: {
        options: creators.options,
        loading: creators.isLoading,
        showSearch: { filterOption: false, onSearch: creators.onSearch }
      }
    },
    {
      title: '创建人',
      dataIndex: 'creator',
      search: false,
      render: (_, row) => <UserAvatarName name={row.creator.name} avatar={row.creator.avatar} />,
      width: '9rem'
    },
    {
      title: '创建时间',
      dataIndex: 'createdAtRange',
      fieldProps: { presets: RANGE_PRESETS },
      valueType: 'dateRange',
      hideInTable: true
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      valueType: 'dateTime',
      search: false,
      sorter: true,
      sortOrder: search.sortOrder === 'asc' ? 'ascend' : 'descend',
      width: '11rem'
    },
    {
      align: 'center',
      title: '操作',
      key: 'actions',
      valueType: 'option',
      fixed: 'right',
      width: '8rem',
      render: (_, row) =>
        canManage ? (
          <Space>
            <Authorized permissions={['expense.update']}>
              <Button type="link" size="small" onClick={() => setEditing(row)}>
                编辑
              </Button>
            </Authorized>
            <Popconfirm
              title="删除这条支出记录？"
              description="删除后不再计入报表。"
              onConfirm={() => removeRows([row])}
            >
              <Authorized permissions={['expense.delete']}>
                <Button type="link" size="small" danger loading={remove.isPending}>
                  删除
                </Button>
              </Authorized>
            </Popconfirm>
          </Space>
        ) : null
    }
  ]
  return (
    <PageContainer title="支出账单" content="记录各主体的支出，保存后按应付日期计入报表。">
      {query.isError && <Alert type="error" showIcon title={query.error.message} />}
      <ProTable<ExpenseRow, Filters>
        rowKey="id"
        columns={columns}
        dataSource={query.data?.data ?? []}
        loading={query.isFetching}
        scroll={{ x: 'max-content' }}
        formRef={table.formRef}
        form={{ initialValues: table.formInitialValues }}
        onSubmit={table.onSubmit}
        onReset={table.onReset}
        pagination={{ ...table.pagination, total: query.data?.total ?? 0 }}
        onChange={
          /* eslint-disable max-params -- Ant Design Table 的回调签名由组件定义。 */
          (_, __, sorter, extra) => {
            if (extra.action === 'sort' && !Array.isArray(sorter) && sorter.field === 'createdAt') {
              void navigate({
                search: {
                  ...search,
                  current: 1,
                  sortField: 'createdAt',
                  sortOrder: sorter.order === 'ascend' ? 'asc' : 'desc'
                }
              })
            }
          }
          /* eslint-enable max-params */
        }
        columnsState={{ persistenceKey: 'super-mega:expense-records:columns:v1', persistenceType: 'localStorage' }}
        options={{ reload: () => query.refetch() }}
        search={{ defaultCollapsed: true }}
        headerTitle={`支出记录（${query.data?.total ?? 0}）`}
        rowSelection={
          canManage
            ? { selectedRowKeys: selected.map((row) => row.id), onChange: (_, rows) => setSelected(rows) }
            : undefined
        }
        toolBarRender={() =>
          canManage
            ? [
                <Authorized key="remove" permissions={['expense.delete']}>
                  <Button
                    key="remove"
                    danger
                    disabled={!selected.length}
                    onClick={() =>
                      modal.confirm({
                        title: `删除选中的 ${selected.length} 条支出记录？`,
                        content: '删除后不再计入报表。',
                        okButtonProps: { danger: true },
                        onOk: () => removeRows(selected)
                      })
                    }
                  >
                    批量删除
                  </Button>
                </Authorized>,
                <Authorized key="import" permissions={['expense.import']}>
                  <Button key="import" onClick={() => setImporting(true)}>
                    导入 Excel
                  </Button>
                </Authorized>,
                <Authorized key="create" permissions={['expense.create']}>
                  <Button key="create" type="primary" onClick={() => setEditing('new')}>
                    新建支出账单
                  </Button>
                </Authorized>
              ]
            : []
        }
      />
      {editing && (
        <ExpenseEditor
          key={editing === 'new' ? 'new' : editing.id}
          editing={editing}
          canCreateBusiness={session.isSuperAdmin || session.permissions.includes('expenseBusiness.create')}
          onClose={() => setEditing(null)}
          onSaved={refresh}
        />
      )}
      {importing && <ExpenseImport onClose={() => setImporting(false)} onSaved={refresh} />}
    </PageContainer>
  )
}
